/**
 * Utility functions for video processing and manipulation
 */

/**
 * Extracts the last frame from a video URL as a base64 image
 * @param videoUrl - The URL of the video to extract frame from
 * @param seekTime - Optional seek time (defaults to near end of video)
 * @returns Promise<string> - Base64 encoded image data
 */
export const extractLastFrameAsBase64 = async (
  videoUrl: string,
  seekTime?: number
): Promise<string> => {
  console.log("Starting frame extraction from:", videoUrl);

  // First, fetch the video as a blob to avoid CORS issues
  let blob: Blob;
  try {
    const response = await fetch(videoUrl);
    if (!response.ok) {
      throw new Error(
        `Failed to fetch video: ${response.status} ${response.statusText}`
      );
    }
    blob = await response.blob();
    console.log("Video blob fetched successfully, size:", blob.size);
  } catch (error) {
    console.error("Failed to fetch video blob:", error);
    throw new Error(`Could not download video: ${error}`);
  }

  // Create object URL from blob
  const objectUrl = URL.createObjectURL(blob);

  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not get canvas context"));
      return;
    }

    let hasResolved = false;
    let timeoutId: NodeJS.Timeout;

    const cleanup = () => {
      try {
        video.pause();
        video.removeAttribute("src");
        video.load();
        video.remove();
        canvas.remove();
        URL.revokeObjectURL(objectUrl);
        if (timeoutId) clearTimeout(timeoutId);
      } catch (e) {
        console.error("Cleanup error:", e);
      }
    };

    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";

    video.onloadedmetadata = () => {
      console.log("Video metadata loaded:", {
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      });

      if (video.videoWidth === 0 || video.videoHeight === 0) {
        cleanup();
        reject(new Error("Invalid video dimensions"));
        return;
      }

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Seek to near the end of the video
      const targetTime =
        seekTime !== undefined ? seekTime : Math.max(0, video.duration - 0.5);
      video.currentTime = targetTime;
      console.log("Seeking to time:", targetTime);
    };

    video.onseeked = () => {
      if (hasResolved) return;
      hasResolved = true;

      try {
        console.log("Video seeked, drawing frame to canvas...");

        // Draw the current frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to base64
        const base64Image = canvas.toDataURL("image/jpeg", 0.9);

        cleanup();
        console.log("Frame extracted successfully");
        resolve(base64Image);
      } catch (error) {
        console.error("Error during frame extraction:", error);
        cleanup();
        reject(new Error(`Failed to draw frame: ${error}`));
      }
    };

    video.onerror = (e) => {
      if (hasResolved) return;
      hasResolved = true;

      console.error("Video error event:", e, {
        error: video.error,
        networkState: video.networkState,
        readyState: video.readyState,
      });

      cleanup();
      reject(
        new Error(
          `Video element error: ${video.error?.message || "Unknown error"}`
        )
      );
    };

    // Set a timeout to prevent hanging
    timeoutId = setTimeout(() => {
      if (hasResolved) return;
      hasResolved = true;

      console.error("Video loading timeout - readyState:", video.readyState);
      cleanup();
      reject(new Error("Video loading timeout - took longer than 30 seconds"));
    }, 30000);

    // Start loading the video
    try {
      video.src = objectUrl;
      video.load();
      console.log("Video loading started with object URL");
    } catch (error) {
      cleanup();
      reject(new Error(`Failed to load video: ${error}`));
    }
  });
};

/**
 * Gets video duration without fully loading the video
 * @param videoUrl - The URL of the video
 * @returns Promise<number> - Duration in seconds
 */
export const getVideoDuration = async (videoUrl: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");

    video.onloadedmetadata = () => {
      const duration = video.duration;
      video.remove();
      resolve(duration);
    };

    video.onerror = () => {
      reject(new Error("Failed to load video metadata"));
    };

    video.preload = "metadata";
    video.src = videoUrl;
  });
};

/**
 * Checks if a video has audio track
 * @param videoUrl - The URL of the video
 * @returns Promise<boolean> - True if video has audio, false otherwise
 */
export const hasAudioTrack = async (videoUrl: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const videoEl = document.createElement("video");

    // Wait until metadata is loaded
    videoEl.onloadedmetadata = () => {
      let hasAudio = false;

      // Try standard API
      if (
        (videoEl as any).audioTracks &&
        (videoEl as any).audioTracks.length > 0
      ) {
        hasAudio = true;
      }
      // Firefox-specific
      else if ((videoEl as any).mozHasAudio) {
        hasAudio = true;
      } else {
        hasAudio = false;
      }

      console.log(`Audio check: ${videoUrl.slice(-20)} = ${hasAudio}`);
      videoEl.remove();
      resolve(hasAudio);
    };

    videoEl.onerror = () => {
      videoEl.remove();
      resolve(false);
    };

    setTimeout(() => {
      videoEl.remove();
      resolve(false);
    }, 3000);

    videoEl.preload = "metadata";
    videoEl.crossOrigin = "anonymous";
    videoEl.src = videoUrl;
  });
};

/**
 * Detects if the current device is mobile and returns mobile-optimized video attributes
 * @returns Object with mobile-specific video attributes
 */
export const getMobileVideoAttributes = () => {
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  return {
    isMobile,
    isIOS,
    isSafari,
    // Mobile-optimized attributes
    attributes: {
      playsInline: true,
      "webkit-playsinline": "true",
      muted: true, // Required for autoplay on mobile
      preload: isMobile ? "metadata" : "auto", // Save bandwidth on mobile
      controls: true,
      // Remove crossOrigin for mobile to avoid CORS issues
      ...(isMobile ? {} : { crossOrigin: "anonymous" }),
    },
  };
};

/**
 * Creates a mobile-friendly video element with proper error handling
 * @param videoUrl - The URL of the video
 * @param options - Additional options for video configuration
 * @returns HTMLVideoElement configured for mobile compatibility
 */
export const createMobileOptimizedVideo = (
  videoUrl: string,
  options: {
    onError?: (error: Event) => void;
    onLoadedData?: () => void;
    onCanPlay?: () => void;
    className?: string;
  } = {}
) => {
  const video = document.createElement("video");
  const mobileAttrs = getMobileVideoAttributes();

  // Apply mobile-optimized attributes
  Object.entries(mobileAttrs.attributes).forEach(([key, value]) => {
    if (typeof value === "boolean") {
      if (value) video.setAttribute(key, "");
    } else {
      video.setAttribute(key, value);
    }
  });

  // Set source
  video.src = videoUrl;

  // Apply custom options
  if (options.className) {
    video.className = options.className;
  }

  // Enhanced error handling for mobile
  video.onerror = (e: Event | string) => {
    console.error("Mobile video loading error:", {
      error: e,
      videoUrl,
      userAgent: navigator.userAgent,
      isMobile: mobileAttrs.isMobile,
      isIOS: mobileAttrs.isIOS,
      isSafari: mobileAttrs.isSafari,
    });

    if (options.onError) {
      // Convert string errors to Event-like objects for consistency
      const errorEvent = typeof e === "string" ? new Event("error") : e;
      options.onError(errorEvent);
    }
  };

  // Success callbacks
  if (options.onLoadedData) {
    video.onloadeddata = options.onLoadedData;
  }

  if (options.onCanPlay) {
    video.oncanplay = options.onCanPlay;
  }

  return video;
};

/**
 * Creates a thumbnail from a video at a specific time
 * @param videoUrl - The URL of the video
 * @param timeInSeconds - Time to capture thumbnail at
 * @param width - Optional width for thumbnail (maintains aspect ratio)
 * @returns Promise<string> - Base64 encoded thumbnail
 */
export const createVideoThumbnail = async (
  videoUrl: string,
  timeInSeconds: number = 1,
  width?: number
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Could not get canvas context"));
      return;
    }

    video.crossOrigin = "anonymous";
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      const aspectRatio = video.videoWidth / video.videoHeight;

      if (width) {
        canvas.width = width;
        canvas.height = width / aspectRatio;
      } else {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
      }

      video.currentTime = Math.min(timeInSeconds, video.duration - 0.1);
    };

    video.onseeked = () => {
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const base64Image = canvas.toDataURL("image/jpeg", 0.8);

        video.remove();
        canvas.remove();

        resolve(base64Image);
      } catch (error) {
        reject(new Error(`Failed to create thumbnail: ${error}`));
      }
    };

    video.onerror = () => {
      reject(new Error("Failed to load video for thumbnail"));
    };

    video.src = videoUrl;
    video.load();
  });
};

/**
 * Checks if a URL points to an image file
 * @param url - The URL to check
 * @returns boolean - True if URL is an image, false otherwise
 */
export const isImageUrl = (url: string): boolean => {
  if (!url) return false;

  // Check file extension
  const imageExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".bmp",
    ".svg",
  ];
  const urlLower = url.toLowerCase();

  return imageExtensions.some((ext) => urlLower.includes(ext));
};

/**
 * Gets the appropriate file extension for a media URL
 * @param url - The URL to check
 * @returns string - File extension (.jpg, .png, .mp4, etc.)
 */
export const getFileExtension = (url: string): string => {
  if (!url) return ".mp4";

  const urlLower = url.toLowerCase();

  // Check for image extensions first
  const imageExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".bmp",
    ".svg",
  ];
  for (const ext of imageExtensions) {
    if (urlLower.includes(ext)) {
      return ext;
    }
  }

  // Check for video extensions
  const videoExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm"];
  for (const ext of videoExtensions) {
    if (urlLower.includes(ext)) {
      return ext;
    }
  }

  // Default to .mp4 if no extension found
  return ".mp4";
};
